import { Grid } from "@material-ui/core";
import { Button, FlashMessage, Input, Modal } from "../../common";
import React, { useEffect, useState } from "react";
import WebsiteIcon from "../icons/website-icon.svg";
import InstagramIcon from "../icons/instagram-icon.svg";
import TwitterIcon from "../icons/x-icon.svg";
import YoutubeIcon from "../icons/youtube-icon.svg";
import LinkedInIcon from "../icons/linkedin-icon.svg";
import MediumIcon from "../icons/medium-icon.svg";
import SocialIcon from "../icons/social-icon.svg";
import FacebookIcon from "../icons/facebook-icon.svg";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import { updateCandidateSocialLinksRequest } from "../../../reducers/candidate/candidate.reducer";
import EditIcon from "../icons/edit-icon.svg";

interface FormValues {
  website?: string;
  instagram?: string;
  xcom?: string;
  youTube?: string;
  linkedIn?: string;
  medium?: string;
  facebook?: string;
}

interface SocialLinksSectionProps {
  social_links: FormValues;
  isPublicPage?: boolean;
}

function SocialLinksSection({ social_links, isPublicPage }: SocialLinksSectionProps) {
  const [socialModal, setSocialModal] = useState(false);
  const [localData, setLocalData] = useState<FormValues>(social_links);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<{ social_links: FormValues }>({
    defaultValues: {
      social_links: social_links || {
        website: "",
        instagram: "",
        xcom: "",
        youTube: "",
        linkedIn: "",
        medium: "",
        facebook: "",
      },
    },
  });

  const { candidateViewFlag, message: candidateMessage } = useSelector(
    ({ candidate }: rootReducersState) => ({
      candidateViewFlag: candidate.candidates.candidateViewFlag,
      message: candidate.candidates.message,
    })
  );

  const openSocialLinkModal = () => {
    if (isPublicPage) return;
    const updatedLink = watch("social_links");
    reset({ social_links: updatedLink });
    setSocialModal(true);
  };

  const closeSocialLinkModal = () => {
    const updatedLink = watch("social_links");
    reset({ social_links: updatedLink });

    // eslint-disable-next-line
    setSocialModal(false);
  };

  useEffect(() => {
    reset({ social_links });
    const updatedData = watch("social_links");
    setLocalData(updatedData);

    // eslint-disable-next-line
  }, [social_links, reset]);

  const onSubmit = (data: any) => {
    const payload = {
      social_links: data.social_links,
    };

    dispatch(updateCandidateSocialLinksRequest(payload));
  };

  useEffect(() => {
    if (candidateViewFlag === "social_success") {
      FlashMessage("Social links updated successfully");
      const updatedData = watch("social_links");
      setLocalData(updatedData);
      reset({ social_links: updatedData });
      setSocialModal(false);
    }
    if (candidateViewFlag === "social_failed") {
      FlashMessage(
        candidateMessage || "Failed to update social links",
        "error"
      );
    }

    // eslint-disable-next-line
  }, [candidateViewFlag, candidateMessage]);

  const getSocialIcon = (key: string) => {
    switch (key) {
      case "website":
        return WebsiteIcon;
      case "instagram":
        return InstagramIcon;
      case "xcom":
        return TwitterIcon;
      case "youTube":
        return YoutubeIcon;
      case "linkedIn":
        return LinkedInIcon;
      case "medium":
        return MediumIcon;
      case "facebook":
        return FacebookIcon;
      default:
        return SocialIcon;
    }
  };

  return (
    <div className="accordion">
      <div className="accordion-head">
        <b>Social Links</b>
        {!isPublicPage &&
          (Object.values(localData).some((link) => !!link) ? (
            <div className="edit-icon" onClick={openSocialLinkModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openSocialLinkModal}>+Add</span>
          ))}
      </div>
      {Object.values(localData).some((link) => !!link) && (
        <div className="accordion-body">
          <div className="social-links-content">
            {Object.values(localData)?.map((link, index) => {
              return (
                link && (
                  <div key={index} className="social-link-item">
                    <div className="social-icon">
                      <img
                        src={getSocialIcon(Object.keys(localData)[index])}
                        alt={`${Object.keys(localData)[index]}-icon`}
                      />
                    </div>
                    <div className="social-link">
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        {link}
                      </a>
                    </div>
                  </div>
                )
              );
            })}
          </div>
        </div>
      )}
      {!isPublicPage && (
        <Modal
          visible={socialModal}
          onClose={closeSocialLinkModal}
          className={`candidate-profile-modal social-link-modal`}
          title={
            Object.values(localData).some((link) => !!link)
              ? "Edit Social Links"
              : "Social Links"
          }
        >
        <div className="modal-body-wrapper">
          <form className="form-wrapper" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>    
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={InstagramIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    externalLabel={{ label: "Instagram" }}
                    placeholder="Instagram"
                    validationObj={errors}
                    required={false}
                    {...register(`social_links.instagram`, {
                      required: {
                        value: false,
                        message: "Please enter the Instagram.",
                      },
                    })}
                  />
                </div>
              </Grid>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={FacebookIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    externalLabel={{ label: "Facebook" }}
                    placeholder="Facebook"
                    validationObj={errors}
                    required={false}
                    {...register(`social_links.facebook`, {
                      required: {
                        value: false,
                        message: "Please enter the Facebook.",
                      },
                    })}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={TwitterIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    externalLabel={{ label: "X.com" }}
                    placeholder="X.com"
                    validationObj={errors}
                    required={false}
                    {...register(`social_links.xcom`, {
                      required: {
                        value: false,
                        message: "Please enter the X.com.",
                      },
                    })}
                  />
                </div>
              </Grid>
              <Grid item sm={6} xs={12}>
                <div className="form-group">
                  <Input
                    inputIcon={
                      <div className="input-icon">
                        <img src={YoutubeIcon} alt="icon" />
                      </div>
                    }
                    className="input-with-icon"
                    externalLabel={{ label: "YouTube" }}
                    placeholder="YouTube"
                    validationObj={errors}
                    required={false}
                    {...register(`social_links.youTube`, {
                      required: {
                        value: false,
                        message: "Please enter the YouTube.",
                      },
                    })}
                  />
                </div>
              </Grid>
            </Grid>
           
            <div className="btn-wrapper">
              <Button
                variant="outlined"
                className="outlined"
                onClick={closeSocialLinkModal}
                color="none"
              >
                Cancel
              </Button>
              <Button className="save-btn" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </div>
        </Modal>
      )}
    </div>
  );
}

export default SocialLinksSection;
